import { useEffect, useMemo, useRef, type RefObject } from "react";
import type { PartialOptions } from "overlayscrollbars";
import { useOverlayScrollbars as useOverlayScrollbarsReact } from "overlayscrollbars-react";

type OverlayScrollDirection = "horizontal" | "vertical" | "both";

interface UseTableOverlayScrollbarsParams {
  enabled?: boolean;
  direction?: OverlayScrollDirection;
  options?: PartialOptions;
  defer?: boolean | { timeout?: number };
}

const getOverflowOptions = (direction: OverlayScrollDirection): PartialOptions["overflow"] => {
  switch (direction) {
    case "horizontal":
      return { x: "scroll", y: "hidden" };
    case "vertical":
      return { x: "hidden", y: "scroll" };
    case "both":
    default:
      return { x: "scroll", y: "scroll" };
  }
};

const overlayHostAttribute = "data-app-overlay-scrollbar-host";

const getMergedOptions = (
  direction: OverlayScrollDirection,
  options?: PartialOptions
): PartialOptions => {
  const directionOverflow = getOverflowOptions(direction);

  return {
    ...options,
    overflow: {
      ...directionOverflow,
      ...options?.overflow,
    },
    scrollbars: {
      theme: "os-theme-dark",
      autoHide: "leave" as const,
      autoHideDelay: 200,
      ...options?.scrollbars,
    },
  };
};

export const useTableOverlayScrollbars = ({
  enabled = true,
  direction = "both",
  options,
  defer = true,
}: UseTableOverlayScrollbarsParams = {}): {
  overlayScrollRef: RefObject<HTMLDivElement | null>;
} => {
  const overlayScrollRef = useRef<HTMLDivElement | null>(null);
  const initializedTargetRef = useRef<HTMLElement | null>(null);
  const mergedOptions = useMemo(() => getMergedOptions(direction, options), [direction, options]);

  const [initialize, instance] = useOverlayScrollbarsReact({
    options: mergedOptions,
    defer,
  });

  useEffect(() => {
    const destroyInstance = () => {
      const currentInstance = instance();
      if (currentInstance) {
        currentInstance.destroy();
      }

      if (initializedTargetRef.current) {
        initializedTargetRef.current.removeAttribute(overlayHostAttribute);
      }

      initializedTargetRef.current = null;
    };

    if (!enabled) {
      destroyInstance();
      return;
    }

    const target = overlayScrollRef.current;
    if (!target) {
      return;
    }

    if (target === initializedTargetRef.current) return;

    destroyInstance();

    target.setAttribute(overlayHostAttribute, "true");

    initialize({
      target,
      elements: {
        viewport: target,
      },
    });

    initializedTargetRef.current = target;

    return () => {
      destroyInstance();
    };
  }, [enabled, initialize, instance]);

  return { overlayScrollRef };
};
